import useCallFlags from '@src/hooks/call/useCallFlags';
import { callEventsBus } from '@src/logic/events/callEventsBus';
import { getChatCallFromLastMessage } from '@src/reducers/chats';
import { selectCallByChatId } from '@src/selectors/calls';
import { Call, CallIncoming, CallOutgoing, CallStatus } from '@src/types/calls';
import { ChatTray } from '@src/types/chats';
import getContactPhoneNumber from '@src/utils/contact';
import { removePhoneNumberPrefix } from '@src/utils/formatPhoneNumber';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const BSUID_PATTERN = /^[A-Z]{2}\.(ENT\.)?[A-Za-z0-9]{1,128}$/;

const useChatCallSync = (chat: ChatTray) => {
  const callInChat = useMemo(
    () => getChatCallFromLastMessage(chat?.lastMessage),
    [chat.lastMessage]
  );
  const call = useSelector(selectCallByChatId(chat.id));
  const callInChatFlags = useCallFlags(callInChat);

  const [isInitiated, setIsInitiated] = useState(false);

  useEffect(() => {
    if (isInitiated) return;

    if (callInChat?.status) {
      setIsInitiated(true);

      const isCallRegistered = call?.id;
      if (
        callInChatFlags.isCallWaitingAgent ||
        callInChatFlags.isCallWaitingAnswer ||
        (callInChatFlags.isCallMissed && !isCallRegistered)
      ) {
        const botPhone = removePhoneNumberPrefix(chat.viaBotAccountAlias);
        const baseCall: Omit<Call, 'callee' | 'caller'> = {
          id: callInChat.id,
          chatId: chat.id,
          direction: callInChat.direction,
          status: callInChat.status,
          event: callInChat.event,
          session: callInChat?.session,
        };

        const contactCmaId = chat.contact.cmaId ?? '';
        const isBsuid = BSUID_PATTERN.test(contactCmaId);
        const contactPhone = isBsuid
          ? removePhoneNumberPrefix(chat.contact.mobileNumber ?? '')
          : getContactPhoneNumber(chat.contact);
        const contactBsuid = isBsuid ? contactCmaId : undefined;

        if (callInChatFlags.isCallOutgoing)
          callEventsBus.emit(
            baseCall.status,
            {
              ...baseCall,
              callee: {
                phoneNumber: contactPhone,
                name: chat.contact.fullName,
                ...(contactBsuid && { bsuid: contactBsuid }),
              },
              caller: {
                phoneNumber: botPhone,
                whatsappPhoneNumberId: chat.viaBotAccount,
              },
            } as CallOutgoing,
            { sync: true }
          );

        if (callInChatFlags.isCallIncoming)
          callEventsBus.emit(
            baseCall.status,
            {
              ...baseCall,
              callee: {
                phoneNumber: botPhone,
                whatsappPhoneNumberId: chat.viaBotAccount,
              },
              caller: {
                phoneNumber: contactPhone,
                name: chat.contact.fullName,
                ...(contactBsuid && { bsuid: contactBsuid }),
              },
            } as CallIncoming,
            { sync: true }
          );
      } else if (call?.id && callInChatFlags.isCallMissed) {
        callEventsBus.emit(CallStatus.MISSED, call, { sync: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.id, callInChat?.status]);

  return call;
};

export default useChatCallSync;
